import { useState, useEffect, useRef, useCallback, useMemo } from "react";
import { createThread, getThreads } from "../../../../api/chat";
import { formatUserNowTime } from "../utils/chatHelpers";

const useChatThreads = ({
  threadType = "private",
  selectedModel = "",
} = {}) => {
  const [threads, setThreads] = useState([]);
  const [localThreads, setLocalThreads] = useState([]);
  const [currentThreadId, setCurrentThreadId] = useState(null);
  const [persistedThreadIds, setPersistedThreadIds] = useState([]);

  const localThreadIds = useMemo(
    () => localThreads.map((t) => t.id),
    [localThreads],
  );

  const allThreads = useMemo(() => {
    const serverMapped = threads
      .map((t) => ({
        id: t.thread_id,
        label: t.title || "Thread",
        thread_type: t.thread_type || "private",
        isLocal: false,
      }))
      .filter((t) => t.id != null);

    return [
      ...localThreads,
      ...serverMapped.filter((s) => !localThreads.some((l) => l.id === s.id)),
    ];
  }, [threads, localThreads]);

  const loadThreads = useCallback(async () => {
    try {
      const res = await getThreads(0, threadType);
      const raw = res?.data?.threads || [];
      setThreads(raw);
    } catch (e) {
      console.error("Error loading threads:", e);
      setThreads([]);
    }
  }, [threadType]);

  const pendingLocalIdRef = useRef(null);
  const localThreadIdsRef_hook = useRef(localThreadIds);
  useEffect(() => {
    localThreadIdsRef_hook.current = localThreadIds;
  }, [localThreadIds]);

  const createNewThread = useCallback(() => {
    const existing = localThreads.find((t) => t.isLocal);
    if (existing) {
      setCurrentThreadId(existing.id);
      pendingLocalIdRef.current = null;
      return existing.id;
    }

    if (pendingLocalIdRef.current) {
      setCurrentThreadId(pendingLocalIdRef.current);
      return pendingLocalIdRef.current;
    }

    const id = crypto.randomUUID();
    pendingLocalIdRef.current = id;
    const newLocal = {
      id,
      label: "New thread",
      isLocal: true,
      loading: true,
      thread_type: threadType,
    };
    setLocalThreads((prev) => [newLocal, ...prev]);
    // Sync the ref immediately so ensureThreadExists (called in the same
    // synchronous frame) can see this ID before React re-renders.
    localThreadIdsRef_hook.current = [id, ...localThreadIdsRef_hook.current];
    setCurrentThreadId(id);
    return id;
  }, [localThreads]);

  const markThreadPersisted = useCallback((threadId) => {
    if (pendingLocalIdRef.current === threadId) {
      pendingLocalIdRef.current = null;
    }
    setLocalThreads((prev) => prev.filter((t) => t.id !== threadId));
  }, []);

  const isLocalThread = useCallback(
    (threadId) => localThreadIds.includes(threadId),
    [localThreadIds],
  );

  const ensureThreadExists = useCallback(
    async (threadId, firstMessage, threadAge) => {
      if (!threadId || persistedThreadIds.includes(threadId)) return;

      // Use the ref instead of the memoized value — setLocalThreads from
      // createNewThread may not have rendered yet, so the memoized
      // localThreadIds is stale. But we *know* we just created this
      // thread, so also accept any ID that isn't already persisted.
      const knownLocal = localThreadIdsRef_hook.current.includes(threadId);

      if (!knownLocal && !isLocalThread(threadId)) {
        setPersistedThreadIds((prev) =>
          prev.includes(threadId) ? prev : [...prev, threadId],
        );
        return;
      }

      try {
        const res = await createThread(
          threadId,
          firstMessage,
          threadAge,
          formatUserNowTime(),
          threadType,
          selectedModel,
        );

        // Update local thread label with the server-generated title
        const title = res?.data?.thread_data?.title || "New thread";
        setLocalThreads((prev) =>
          prev.map((t) =>
            t.id === threadId ? { ...t, label: title, loading: false } : t,
          ),
        );

        setPersistedThreadIds((prev) =>
          prev.includes(threadId) ? prev : [...prev, threadId],
        );

        // Load server threads, then remove local copy (server version takes over)
        await loadThreads();
        markThreadPersisted(threadId);
      } catch (e) {
        console.error("ensureThreadExists failed:", e);
        setLocalThreads((prev) =>
          prev.map((t) => (t.id === threadId ? { ...t, loading: false } : t)),
        );
      }
    },
    [persistedThreadIds, isLocalThread, markThreadPersisted, loadThreads],
  );

  useEffect(() => {
    loadThreads();
  }, [loadThreads]);

  return {
    allThreads,
    currentThreadId,
    setCurrentThreadId,
    localThreadIds,
    loadThreads,
    createNewThread,
    markThreadPersisted,
    ensureThreadExists,
    isLocalThread,
  };
};

export default useChatThreads;
